/* eslint-disable */
import * as signalR from '@microsoft/signalr';
import { SCALES_SOCKET } from '../../constants/apiConstants';
import {
  SCALE_STATE_NOTIFIED,
  RECEIVE_WEIGHT_MESSAGE,
  CONNECT_TO_SCALE,
  DISCONNECT_FROM_SCALE,
  JOIN_GROUP,
} from '../../constants/appConstants';
import { store } from '../../Main';
import {
  scaleWeightReceived,
  scaleStateNotified,
  setScaleHubState,
} from '../../store/actions/scalesActions';
import { ScaleState } from '../../types';
import { getBridgeBaseUrl } from '../../util/apiUtil';
import { getTokens, isExpired } from '../../util/authUtil';
import { ensureFreshAccessToken, RefreshOutcome } from '../requestService';
import recordLog from '../../util/errorUtil';
import { isAuthFailureError, ReconnectScheduler } from '../../util/socketUtil';

export class ScalesSocket {
  hubConnection?: signalR.HubConnection;
  MAX_JOINRETRIES = 30; // arbitrary 30 retries
  REJOIN_TIMEOUT = 2000; // keeps retrying for 1 minute every 2 seconds
  retryJoinCount = 0;

  scaleId?: string = '';

  mockDataInterval?: number; // Add this line

  /**
   * Owns backoff state (attempt counter + pending timer) for this socket.
   */
  private reconnectScheduler = new ReconnectScheduler();

  constructor() {
    this.hubConnection = undefined;
  }

  async connect(scaleId: string) {
    if (this.hubConnection) {
      return;
    }

    try {
      let { accessToken } = getTokens();
      if (!accessToken) {
        return;
      }

      // An expired token 403s on /negotiate and spins the reconnect loop, so
      // refresh first. transient -> keep backing off; invalid -> user logged out.
      if (isExpired(accessToken)) {
        const refreshOutcome = await ensureFreshAccessToken();
        switch (refreshOutcome) {
          case RefreshOutcome.Refreshed:
            break;
          case RefreshOutcome.Transient:
            // Skip reconnect if a concurrent connect landed a hub during the
            // await (no ConnectionState enum here, so check the handle). PR #1217.
            if (!this.hubConnection) {
              this.reconnect(scaleId);
            }
            return;
          case RefreshOutcome.Invalid:
            return;
          default:
            return;
        }
        accessToken = getTokens().accessToken;
        if (!accessToken) {
          return;
        }
      }

      this.scaleId = scaleId;
      const SCALESSOCKET_URL = `${getBridgeBaseUrl()}${SCALES_SOCKET}`;
      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${SCALESSOCKET_URL}?tk=${accessToken}`, {
          accessTokenFactory: () => accessToken!,
        })
        .configureLogging(signalR.LogLevel.Debug)
        .build();

      this.hubConnection.onclose(() => this.reconnect(scaleId));

      // establish onResponse as the method to call when weight is received
      this.hubConnection.on(RECEIVE_WEIGHT_MESSAGE, (response: any) => {
        this.onResponse(response);
      });

      await this.hubConnection.start();

      // each scale has its own group named for its Id
      await this.retryJoinGroup(scaleId);

      // Full handshake + group join succeeded: reset backoff so a later drop
      // recovers quickly rather than inheriting delay from an earlier outage.
      this.reconnectScheduler.reset();
      store.dispatch(setScaleHubState({ connected: true }));
    } catch (error: any) {
      recordLog('warn', 'Connecting to Scales Socket', error);
      this.scaleId = undefined;
      this.hubConnection = undefined;
      store.dispatch(setScaleHubState({ connected: false }));
      // A 401/403 here is the server refusing a token the client thinks is
      // valid (e.g. auth outage rejecting good tokens, DEV-4179): refresh,
      // then retry -- unless the session is definitively gone.
      if (isAuthFailureError(error)) {
        const refreshOutcome = await ensureFreshAccessToken();
        if (refreshOutcome === RefreshOutcome.Invalid) {
          return;
        }
        // A concurrent connect may have landed a hub during the await; don't
        // orphan it by reconnecting the old scale. PR #1217.
        if (this.hubConnection) {
          return;
        }
      }
      this.reconnect(scaleId);
    }
  }

  async retryJoinGroup(scaleId: string) {
    this.scaleId = scaleId;

    while (this.retryJoinCount < this.MAX_JOINRETRIES) {
      if (
        this.hubConnection &&
        this.hubConnection.state &&
        this.hubConnection.state === signalR.HubConnectionState.Connected
      ) {
        try {
          await this.hubConnection!.invoke(JOIN_GROUP, scaleId);
          this.retryJoinCount = 0; // Reset retry count upon success
          return; // Group joined successfully
        } catch (error) {
          recordLog('warn', 'Error joining group', error);
        }
      }

      // If not connected or joining failed, wait before retrying
      await new Promise(resolve => setTimeout(resolve, this.REJOIN_TIMEOUT));
      this.retryJoinCount++;
    }

    throw new Error('Failed to join group after multiple retries.');
  }

  /**
   * Queues the next reconnect attempt using capped exponential backoff.
   * Performs the immediate "we are no longer connected" side effects
   * (store update + hub handle clear) synchronously so UI observers see
   * the disconnected state right away, then defers the actual reconnect.
   */
  reconnect(scaleId: string) {
    store.dispatch(setScaleHubState({ connected: false }));
    this.hubConnection = undefined;

    this.reconnectScheduler.schedule(() => this.connect(scaleId));
  }

  async disconnect() {
    this.reconnectScheduler.reset();

    if (!this.hubConnection) {
      return;
    }

    try {
      await this.hubConnection.stop();
      store.dispatch(setScaleHubState({ connected: false }));
    } catch (error: any) {
      recordLog('warn', 'Error disconnecting from Scales Socket', error);
    } finally {
      this.scaleId = undefined;
      this.hubConnection = undefined;
    }
  }

  onResponse = (response: any) => {
    const { group, message } = JSON.parse(response);
    if (!this.scaleId || this.scaleId != group) {
      return;
    }

    store.dispatch(scaleWeightReceived(message.scaleId!, message.weight, message.isMoving));
  };

  onScaleStateNotified = (scaleState: ScaleState) => {
    if (!this.scaleId) {
      return;
    }

    store.dispatch(scaleStateNotified(this.scaleId, scaleState));
  };
}

export default new ScalesSocket();
